import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../userContext';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import BeatLoader from 'react-spinners/BeatLoader';
import { TrashIcon, UserPlusIcon, UsersIcon, LockOpenIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import ModalBasic from '../components/ModalBasic';
import { useClient } from '../clientContext';

// Initialize Firebase functions with the correct region
const functions = getFunctions(undefined, 'europe-west1');

// Initialize the function with the specific name
const createUserAndSendPasswordReset = httpsCallable(functions, 'createUserAndSendPasswordReset');
const deleteUser = httpsCallable(functions, 'deleteUser');

export default function Users() {
  const { user, isOwner, businessProfile, openUpgradeModal, updateCompanyProfile } = useUser();
  // Subscriptions retired: lock everyone to a single seat. Agency mode no longer exists.
  const subscriptionType = null;
  const subscriptionStatus = null;
  const entitlements = { agencyMode: false };
  const limits = { maxUsers: 1 };
  const SUB_TYPE = { STUDIO: 'studio', AGENCY: 'agency' };
  const { clients } = useClient();
  const [email, setEmail] = useState('');
  const [selectedWorkspaces, setSelectedWorkspaces] = useState(() => clients.map(c => c.id));
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const userLimit = limits?.maxUsers ?? 1;
  const [showNotification, setShowNotification] = useState(false);
  const [notification, setNotification] = useState({
    type: '', // 'success' | 'error'
    message: ''
  });
  const [enableModal, setEnableModal] = useState({ open: false, userToEnable: null, lockUserId: null });
  const initialActiveUserIdsSet = useRef(false);

  // Keep selectedWorkspaces in sync when clients load/change (default all enabled)
  useEffect(() => {
    if (clients.length > 0) {
      setSelectedWorkspaces(prev => {
        const currentIds = clients.map(c => c.id);
        // Only reset if this is the initial load (prev was empty or had stale IDs)
        if (prev.length === 0 || prev.every(id => !currentIds.includes(id))) {
          return currentIds;
        }
        return prev;
      });
    }
  }, [clients]);

  // Fetch users when the component mounts or when businessProfile changes
  const fetchUsers = async () => {
    if (!businessProfile?.id) return;
    try {
      setLoadingUsers(true);
      const usersQuery = query(
        collection(db, 'users'),
        where('companyId', '==', businessProfile.id)
      );
      const querySnapshot = await getDocs(usersQuery);
      const usersList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotificationMessage('error', 'Failed to load users.');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [businessProfile]);

  // Engage with >1 user: set initial activeUserIds to owner only so we lock the rest
  useEffect(() => {
    if (
      !businessProfile?.id ||
      subscriptionType !== SUB_TYPE.STUDIO ||
      users.length <= 1 ||
      initialActiveUserIdsSet.current
    ) return;
    const activeUserIds = businessProfile.activeUserIds;
    if (Array.isArray(activeUserIds) && activeUserIds.length > 0) return; // already set
    const ownerId = businessProfile.ownerId;
    if (!ownerId) return;
    initialActiveUserIdsSet.current = true;
    const initial = [ownerId];
    updateCompanyProfile({ activeUserIds: initial }).catch((err) => {
      console.error('Failed to set initial activeUserIds:', err);
      initialActiveUserIdsSet.current = false;
    });
  }, [businessProfile, subscriptionType, users, updateCompanyProfile]);

  const showNotificationMessage = (type, message) => {
    setNotification({ type, message });
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 5000);
  };

  const handleDeleteUser = async (userId) => {
    try {
      setDeletingUserId(userId);
      await deleteUser({ userId });
      if (subscriptionType === SUB_TYPE.STUDIO && Array.isArray(businessProfile?.activeUserIds) && businessProfile.activeUserIds.includes(userId)) {
        await updateCompanyProfile({
          activeUserIds: businessProfile.activeUserIds.filter((id) => id !== userId),
        });
      }
      showNotificationMessage('success', 'User removed successfully');
      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotificationMessage('error', 'Unable to remove user. Please try again.');
    } finally {
      setDeletingUserId(null);
    }
  };

  const activeUserIds = Array.isArray(businessProfile?.activeUserIds) ? businessProfile.activeUserIds : [];
  const isEngageWithLocked = subscriptionType === SUB_TYPE.STUDIO && userLimit === 1 && users.length > 1 && activeUserIds.length === 1;
  const activeUsers = isEngageWithLocked ? users.filter((u) => activeUserIds.includes(u.id)) : users;
  const lockedUsers = isEngageWithLocked ? users.filter((u) => !activeUserIds.includes(u.id)) : [];

  const handleEnableUser = (userToEnable) => {
    setEnableModal({ open: true, userToEnable, lockUserId: null });
  };

  const handleConfirmEnable = async () => {
    const { userToEnable, lockUserId } = enableModal;
    if (!userToEnable || !lockUserId || !businessProfile?.activeUserIds) return;
    const newActive = businessProfile.activeUserIds.filter((id) => id !== lockUserId).concat([userToEnable.id]);
    try {
      await updateCompanyProfile({ activeUserIds: newActive });
      showNotificationMessage('success', 'User enabled. They can sign in again.');
      setEnableModal({ open: false, userToEnable: null, lockUserId: null });
    } catch (err) {
      console.error(err);
      showNotificationMessage('error', 'Failed to update. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !firstName || !lastName) {
      showNotificationMessage('error', 'All fields are required');
      return;
    }
    if (users.length >= userLimit) {
      showNotificationMessage('error', `User limit reached (${userLimit}).`);
      return;
    }

    try {
      setAddingUser(true);
      const response = await createUserAndSendPasswordReset({
        email,
        firstName,
        lastName,
        companyId: businessProfile.id,
        // Only restrict if some workspaces are toggled off (not all selected)
        ...(selectedWorkspaces.length > 0 && selectedWorkspaces.length < clients.length && { workspaceIds: selectedWorkspaces }),
      });

      if (response.data) {
        showNotificationMessage('success', 'Invitation sent! They will receive an email to complete their account setup.');
        setEmail('');
        setFirstName('');
        setLastName('');
        setSelectedWorkspaces(clients.map(c => c.id));
        fetchUsers();
      }
    } catch (error) {
      console.error('Error adding user:', error);
      const code = error?.code;
      if (code === 'functions/already-exists') {
        showNotificationMessage('error', 'An account with this email already exists.');
      } else if (code === 'functions/resource-exhausted') {
        showNotificationMessage('error', 'User limit reached. Upgrade your plan or remove a user.');
      } else if (code === 'functions/permission-denied') {
        showNotificationMessage('error', 'Only the account owner can invite users.');
      } else {
        showNotificationMessage('error', 'Unable to add user. Please try again.');
      }
    } finally {
      setAddingUser(false);
    }
  };

  if (!isOwner) {
    return (
      <div className="w-full px-8 py-6">
        <div className="rounded-xl p-6 bg-red-500/15 border border-red-400/30">
          <div>
            <h3 className="text-base font-semibold text-red-300">
              Access Restricted
            </h3>
            <p className="text-sm text-red-200/80 font-medium">
              Only the account administrator can manage users.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Team features are Agency-only
  const hasAgencyAccess = subscriptionType === SUB_TYPE.AGENCY && (subscriptionStatus === 'active' || subscriptionStatus === 'trialing');
  if (!hasAgencyAccess) {
    return (
      <div className="w-full px-8 py-6">
        <div className="rounded-xl p-6 bg-white/5 border border-[var(--admin-border)]">
          <h3 className="text-base font-semibold gch-text-primary">
            Team members require the Team plan
          </h3>
          <p className="text-sm gch-text-secondary font-medium mt-1 mb-4">
            Invite colleagues to help build and manage campaigns across your workspaces. Available on the Team plan.
          </p>
          <ul className="space-y-2 mb-5">
            <li className="flex items-start gap-2 text-sm gch-text-secondary">
              <UsersIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--admin-accent)]" />
              <span>Up to 10 team members per account</span>
            </li>
            <li className="flex items-start gap-2 text-sm gch-text-secondary">
              <UsersIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--admin-accent)]" />
              <span>Control workspace access per user</span>
            </li>
            <li className="flex items-start gap-2 text-sm gch-text-secondary">
              <UsersIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--admin-accent)]" />
              <span>Collaborate on campaigns without sharing credentials</span>
            </li>
          </ul>
          <button
            type="button"
            onClick={openUpgradeModal}
            className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg btn-admin-primary"
          >
            Upgrade to Team
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-8 py-6">
      {/* Notification */}
      {showNotification && (
        <div 
          className={`mb-6 rounded-xl p-4 transition-all duration-300 ${
            notification.type === 'success'
              ? 'bg-green-500/15 border border-green-400/30'
              : 'bg-red-500/15 border border-red-400/30'
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              {notification.type === 'success' ? (
                <svg className="h-5 w-5 text-green-300" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <p className={`font-semibold ${notification.type === 'success' ? 'text-green-200' : 'text-red-300'}`}>
              {notification.message}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Header Section */}
        <div>
          <div className="flex items-center mb-2">
            <h2 className="text-lg font-semibold tracking-tight gch-text-primary">
              User Management
            </h2>
            <span className={`ml-2 gch-pill text-[10px] font-semibold px-2.5 py-0.5 ${
              isEngageWithLocked ? 'text-amber-300' : users.length >= userLimit
                ? 'text-rose-300'
                : 'text-emerald-300'
            }`}>
              {isEngageWithLocked ? `${activeUsers.length} active, ${lockedUsers.length} locked` : `${users.length}/${userLimit} Users`}
            </span>
          </div>
          <p className="text-sm font-medium gch-text-secondary">
            Manage team members who have access to your SmilesFlow account.
          </p>
        </div>

        {/* Active users (Engage with lockout) or all users */}
        <div>
          <div className="mb-4">
            <h3 className="text-base font-semibold gch-text-primary">
              {isEngageWithLocked ? `Active (${activeUsers.length}/1)` : 'Current Users'}
            </h3>
            {isEngageWithLocked && (
              <p className="text-xs gch-text-secondary mt-1">Only active users can sign in. Enable or remove users below to change who has access.</p>
            )}
          </div>

          {loadingUsers ? (
            <div className="flex justify-center items-center py-8">
              <BeatLoader color="#64748B" loading={loadingUsers} />
            </div>
          ) : activeUsers.length === 0 && !isEngageWithLocked ? (
            <div className="text-center py-8 bg-white/5 rounded-lg">
              <p className="text-sm font-medium gch-text-secondary">
                No users have accepted invitations yet.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {(isEngageWithLocked ? activeUsers : users).map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between p-4 rounded-lg transition-all duration-200 gch-bg-card border border-[var(--admin-border)] hover:border-[var(--admin-border)]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold bg-admin-hover text-admin-text-secondary">
                        {(u.firstName || u.email || 'U').charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold gch-text-primary">
                          {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
                        </span>
                        {u.id === businessProfile.ownerId && (
                          <span className="gch-pill text-[10px] font-semibold px-2 py-0.5 text-emerald-300">
                            Admin
                          </span>
                        )}
                      </div>
                      <p className="text-xs font-medium gch-text-secondary">{u.email}</p>
                    </div>
                  </div>
                  {u.id !== businessProfile.ownerId && (
                    <button
                      onClick={() => handleDeleteUser(u.id)}
                      disabled={addingUser || deletingUserId !== null}
                      className={`p-2 rounded-lg transition-all duration-200 border border-red-400/30 text-red-400 hover:bg-red-500/15 hover:border-red-400/50 ${
                        deletingUserId === u.id ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="Remove user"
                    >
                      {deletingUserId === u.id ? (
                        <BeatLoader size={4} color="#DC2626" />
                      ) : (
                        <TrashIcon className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Locked users (Engage only when >1 user) */}
        {isEngageWithLocked && lockedUsers.length > 0 && (
          <div>
            <div className="mb-4">
              <h3 className="text-base font-semibold gch-text-primary flex items-center gap-2">
                <LockClosedIcon className="w-4 h-4 gch-text-secondary" />
                Locked ({lockedUsers.length})
              </h3>
              <p className="text-xs gch-text-secondary mt-1">These users cannot sign in. Enable one to swap with an active user, or remove them.</p>
            </div>
            <div className="space-y-2">
              {lockedUsers.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between p-4 rounded-lg gch-bg-card border border-[var(--admin-border)] opacity-90"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold bg-white/10 gch-text-secondary">
                      {(u.firstName || u.email || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <span className="text-sm font-semibold gch-text-secondary">
                        {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
                      </span>
                      <p className="text-xs gch-text-muted">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleEnableUser(u)}
                      className="p-2 rounded-lg border border-green-400/30 text-green-400 hover:bg-green-500/15 transition-colors"
                      title="Enable this user (choose who to lock)"
                    >
                      <LockOpenIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(u.id)}
                      disabled={deletingUserId !== null}
                      className={`p-2 rounded-lg border border-red-400/30 text-red-400 hover:bg-red-500/15 ${
                        deletingUserId === u.id ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="Remove user"
                    >
                      {deletingUserId === u.id ? (
                        <BeatLoader size={4} color="#DC2626" />
                      ) : (
                        <TrashIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Enable user modal */}
        <ModalBasic
          id="enable-user-modal"
          title={`Enable ${enableModal.userToEnable?.firstName || enableModal.userToEnable?.email || 'user'}?`}
          modalOpen={enableModal.open && !!enableModal.userToEnable}
          setModalOpen={(open) => { if (!open) setEnableModal({ open: false, userToEnable: null, lockUserId: null }); }}
        >
          <div className="p-6">
            <p className="text-sm gch-text-secondary mb-4">
              Choose which active user to lock. They will lose access until you enable them again.
            </p>
            <select
              value={enableModal.lockUserId || ''}
              onChange={(e) => setEnableModal((prev) => ({ ...prev, lockUserId: e.target.value || null }))}
              className="w-full rounded-lg border border-[var(--admin-border)] gch-bg-card gch-text-primary py-2 px-3 text-sm mb-4"
            >
              <option value="">Select user to lock…</option>
              {activeUsers.filter((u) => u.id !== businessProfile.ownerId).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
                </option>
              ))}
            </select>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setEnableModal({ open: false, userToEnable: null, lockUserId: null })}
                className="btn-admin-outline btn-admin-md"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmEnable}
                disabled={!enableModal.lockUserId}
                className="btn-admin-primary btn-admin-md disabled:opacity-50"
              >
                Enable & lock other
              </button>
            </div>
          </div>
        </ModalBasic>

        {/* Add User Form */}
        <div className="bg-white/5 rounded-lg p-6">
          <div className="mb-4">
            <h3 className="text-base font-semibold gch-text-primary">
              Invite New User
            </h3>
          </div>
          <p className="text-sm font-medium gch-text-secondary mb-6">
            They will receive an email with instructions to set up their account.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="block text-sm font-semibold gch-text-primary">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  disabled={addingUser || deletingUserId !== null || users.length >= userLimit}
                  className="block w-full rounded-lg shadow-sm sm:text-sm transition-all duration-200 font-medium py-2.5 px-3 gch-input-ghost border border-[var(--admin-border)] gch-text-primary placeholder:gch-text-muted focus:border-[var(--admin-accent)] focus:ring-[var(--admin-accent)] disabled:bg-white/5 disabled:gch-text-muted"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold gch-text-primary">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  disabled={addingUser || deletingUserId !== null || users.length >= userLimit}
                  className="block w-full rounded-lg shadow-sm sm:text-sm transition-all duration-200 font-medium py-2.5 px-3 gch-input-ghost border border-[var(--admin-border)] gch-text-primary placeholder:gch-text-muted focus:border-[var(--admin-accent)] focus:ring-[var(--admin-accent)] disabled:bg-white/5 disabled:gch-text-muted"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold gch-text-primary">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                disabled={addingUser || deletingUserId !== null || users.length >= userLimit}
                className="block w-full rounded-lg shadow-sm sm:text-sm transition-all duration-200 font-medium py-2.5 px-3 gch-input-ghost border border-[var(--admin-border)] gch-text-primary placeholder:gch-text-muted focus:border-[var(--admin-accent)] focus:ring-[var(--admin-accent)] disabled:bg-white/5 disabled:gch-text-muted"
              />
            </div>

            {clients.length > 0 && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold gch-text-primary">
                  Workspace Access
                </label>
                <p className="text-xs gch-text-secondary">
                  All workspaces enabled by default. Toggle off to restrict access.
                </p>
                <div className="space-y-1.5 mt-2">
                  {clients.map(ws => {
                    const enabled = selectedWorkspaces.includes(ws.id);
                    return (
                      <label key={ws.id} className="flex items-center justify-between cursor-pointer py-1.5">
                        <span className={`text-sm ${enabled ? 'gch-text-primary' : 'gch-text-muted'}`}>{ws.name}</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={enabled}
                          disabled={addingUser}
                          onClick={() => {
                            setSelectedWorkspaces(prev =>
                              enabled ? prev.filter(id => id !== ws.id) : [...prev, ws.id]
                            );
                          }}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--admin-accent)] focus:ring-offset-2 focus:ring-offset-[var(--admin-bg)] disabled:opacity-50 disabled:cursor-not-allowed ${
                            enabled ? 'bg-[var(--admin-accent)]' : 'bg-white/20'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              enabled ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-end gap-3">
              <button
                type="submit"
                disabled={!email || !firstName || !lastName || addingUser || deletingUserId !== null || users.length >= userLimit}
                className="btn-admin-primary btn-admin-md min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingUser ? (
                  <BeatLoader size={8} color="#ffffff" />
                ) : (
                  'Send Invitation'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

    </div>
  );
}
